import { useAppSelector } from '@app/core/hooks/redux-hooks';
import Decimal from 'decimal.js';
import { FC, Fragment, useCallback } from 'react';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { enumKeyByValue } from '@app/shared/helpers';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { TextTransform } from '@app/shared/enums/text-transform.enum';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { Pwerm2WaterfallPartialTableProps } from '../pwerm-waterfall-utils/pwerm2-waterfall-shared';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSectionValuesPerMultiple, {
  SymbolType,
} from '../../components/TableSectionValuesPerMultiple';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseYearInstrumentFirstTrancheValue,
  getWaterfallCaseYearInstrumentTrancheConditionValue,
  getWaterfallCaseYearInstrumentTrancheValue,
  getWaterfallCaseYearInstrumentValue,
  getWaterfallCaseYearMultipleInstrumentFirstTrancheValue,
  getWaterfallCaseYearMultipleInstrumentTrancheValue,
  getWaterfallCaseYearMultipleInstrumentValue,
  getWaterfallCaseYearMultipleValue,
  getWaterfallCaseYearValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { Pwerm2WaterfallTotalPayoutSummaryPartialTable } from '../total-payout-summary/Pwerm2WaterfallTotalPayoutSummaryPartialTable';
import {
  calculatedMultipleValueFormatterProps,
  getCalculatedProjectValueInPercent,
  numberValueFormatter,
  percentageValueFormatterProps,
} from '@app/shared/formatters/number-value-formatter';
import { selectOrdinaryEquityInstruments } from '@app/core/store/capital-structure-slice-selectors';
import styles from '@app/modules/projects/outputs/outputs-table.module.scss';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { selectSortedCasesStructure } from '@app/core/store/project-slice-selectors';
import { Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionMomHurdleDetailsDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import {
  WaterfallSectionsErf,
  IRRHurdleCalculationsNavigatedSourcePage,
  SponsorIrrAndHurdleTab,
  WaterfallSections,
  TargetMetric,
  PayoutDriver,
  Operator,
  CapType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { buildFocusModeSearchParams } from '@app/shared/hooks/focus-mode';
import { FocusModeLocationState } from '@app/shared/hooks/focus-mode/focus-mode-types';
import { useLocation } from 'react-router-dom';

import { Pwerm2WaterfallToeHurdleRows } from './Pwerm2WaterfallToeHurdleRows';

export const Pwerm2WaterfallOrdinaryEquityPartialTable: FC<Pwerm2WaterfallPartialTableProps> = ({
  columnsCount,
}): JSX.Element => {
  const { l } = useLocale();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const interpolationOption = {
    escapeValue: false,
  };
  const equityInstruments = useAppSelector(selectOrdinaryEquityInstruments);
  const sortedEquityInstruments = useInstrumentSort(equityInstruments);
  const casesStructure = useAppSelector(selectSortedCasesStructure);
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.OrdinaryEquity);
  const { generatePath, navigate } = usePwcNavigate();
  const location = useLocation();

  const getShouldRenderCumulativeInfusionRow = useCallback(
    (
      trancheHasMoM: boolean,
      trancheHasIRR: boolean,
      trancheId: string,
      momIndex: number,
      instrumentId: string
    ) => {
      return (
        (trancheHasMoM || trancheHasIRR) &&
        casesStructure.some((caseItem) =>
          caseItem.forecasts.some((forecast) => {
            const value = getWaterfallCaseYearInstrumentTrancheConditionValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrumentId,
              trancheId,
              momIndex,
              (condition) => condition?.cumulativeInfusions ?? 0
            );
            return value !== 0 && value !== null && value !== undefined;
          })
        )
      );
    },
    [casesStructure, results]
  );

  const handleIrrCellClick = (
    caseId: string,
    forecastId: string,
    instrumentId: string,
    conditionReference: string,
    title: string
  ) => {
    const exitDate = casesStructure
      .find((caseItem) => caseItem.caseId === caseId)
      ?.forecasts.find((forecast) => forecast.forecastId === forecastId)?.forecastYear;

    if (!exitDate) {
      return;
    }

    const selections = {
      type: SponsorIrrAndHurdleTab.IRRHurdleCalculations as const,
      case: caseId,
      year: exitDate,
      instrument: instrumentId,
      hurdle: conditionReference,
      hurdleTitle: title,
      source: IRRHurdleCalculationsNavigatedSourcePage.PwermWaterfall,
    };
    const params = buildFocusModeSearchParams(selections);
    const newPath = generatePath(
      `../${RouteConstants.SponsorIRRAndHurdle}/${RouteConstants.IRRHurdleCalculations}/${caseId}`
    );
    navigate(`${newPath}?${params}`, {
      state: { returnPath: location.pathname } as FocusModeLocationState,
    });
  };

  return (
    <>
      <tbody data-testid="ordinary-equity-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_OrdinaryEquityTitle')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.OrdinaryEquity)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.OrdinaryEquity]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.OrdinaryEquity],
        })}
        data-testid="ordinary-equity-section">
        <TableSectionValuesPerMultiple
          title={l('_OrdinaryEquity')}
          renderCell={({ caseItem, forecast, multipleId }) => {
            return (
              <CellValue
                {...amountRules}
                value={getWaterfallCaseYearMultipleValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  multipleId!,
                  (multiple) => multiple.totalOrdinaryEquityProceeds
                )}
                dataTestId={`ordinary-equity-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
              />
            );
          }}
        />
        <TableSectionValuesPerForecast
          title={l('_WeightedOrdinaryEquity')}
          highlight
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.weightedTotalOrdinaryEquityProceeds
              )}
              dataTestId={`weighted-ordinary-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
          additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
        />
        {sortedEquityInstruments.map((instrument) => (
          <Fragment key={instrument.instrumentId}>
            <tr>
              <th
                className="table-primary__cell--header-quaternary"
                data-testid={`instrument-header-${instrument.instrumentId}`}>
                {instrument.instrumentNarrative}
              </th>
              <th colSpan={columnsCount - 1} className="table-primary__cell--header-quaternary" />
            </tr>
            {instrument.payoutLogic?.length === 0 && (
              <>
                <TableSectionValuesPerForecast
                  title={l('_PcShareholding')}
                  renderCell={({ caseItem, forecast }) => {
                    // required as each instrument contains at least one tranche, and this
                    // relates to a scenario where the source implies that there are no tranches
                    const value = getCalculatedProjectValueInPercent(
                      getWaterfallCaseYearInstrumentFirstTrancheValue(
                        results,
                        caseItem.caseId,
                        forecast.forecastId,
                        instrument.instrumentId,
                        (tranche) => tranche.shareholdingPc
                      )
                    );

                    return (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={value}
                        dataTestId={`shareholding-pc-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
                      />
                    );
                  }}
                />
                <TableSectionValuesPerMultiple
                  title={l('_PcParticipation')}
                  testId={instrument.instrumentNarrative}
                  renderCell={({ caseItem, forecast, multipleId }) => {
                    const value = getCalculatedProjectValueInPercent(
                      getWaterfallCaseYearMultipleInstrumentFirstTrancheValue(
                        results,
                        caseItem.caseId,
                        forecast.forecastId,
                        multipleId!,
                        instrument.instrumentId,
                        (tranche) => tranche.totalParticipationPc
                      )
                    );

                    const plusSign = Boolean(
                      getWaterfallCaseYearMultipleInstrumentValue(
                        results,
                        caseItem.caseId,
                        forecast.forecastId,
                        multipleId!,
                        instrument.instrumentId,
                        (instrument) => instrument.hasReceivedEnhancedParticipation
                      )
                    );

                    return (
                      <CellValue
                        {...enteredPercentageViewRules}
                        plusSign={plusSign}
                        value={value}
                        dataTestId={`total-participation-pc-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                      />
                    );
                  }}
                  symbolTooltipInfo={{
                    instrumentId: instrument.instrumentId,
                    results,
                    symbolType: SymbolType.PlusSign,
                  }}
                />
                <TableSectionValuesPerMultiple
                  title={l('_DistributionsPaid')}
                  testId={instrument.instrumentNarrative}
                  renderCell={({ caseItem, forecast, multipleId }) => {
                    const value = getWaterfallCaseYearMultipleInstrumentValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      multipleId!,
                      instrument.instrumentId,
                      (instrument) => instrument.totalDistributions
                    );

                    return (
                      <CellValue
                        {...amountRules}
                        value={value}
                        dataTestId={`distributions-paid-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                      />
                    );
                  }}
                />
                <TableSectionValuesPerForecast
                  title={l('_WeightedDistributions')}
                  highlight
                  additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                  additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                  renderCell={({ caseItem, forecast }) => {
                    const value = getWaterfallCaseYearInstrumentValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrument.instrumentId,
                      (instrument) => instrument.weightedTotalDistributions
                    );

                    return (
                      <CellValue
                        {...amountRules}
                        strong
                        value={value}
                        dataTestId={`weighted-distributions-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
                      />
                    );
                  }}
                />
              </>
            )}
            {(instrument.payoutLogic?.length ?? 0) > 0 && (
              <>
                {instrument.payoutLogic!.map((tranche, index) => {
                  const trancheHasIRR = tranche.conditions.some(
                    (c) =>
                      c.targetMetric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR)
                  );

                  const trancheHasMoM = tranche.conditions.some(
                    (c) =>
                      c.targetMetric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalMoM)
                  );

                  const trancheHasConjunction = Boolean(tranche.conjunction);

                  const isIncrementalHurdleUsed =
                    tranche.payoutCalculation.payoutDriver ===
                    enumKeyByValue(PayoutDriver, PayoutDriver.Incremental);

                  // each condition contains momHurdleDetails, and some values are duplicated across each, and so we can rely on just the first one
                  const momIndex = tranche.conditions.findIndex(
                    (c) =>
                      c.targetMetric ===
                        enumKeyByValue(TargetMetric, TargetMetric.InstitutionalMoM) ||
                      c.targetMetric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR)
                  );
                  const shouldRenderCumulativeInfusionRow = getShouldRenderCumulativeInfusionRow(
                    trancheHasMoM,
                    trancheHasIRR,
                    tranche.id,
                    momIndex,
                    instrument.instrumentId
                  );

                  const toeConditions = (tranche.conditions ?? []).filter(
                    (c) =>
                      c.targetMetric ===
                      enumKeyByValue(TargetMetric, TargetMetric.TotalOrdinaryEquity)
                  );

                  const hasToeDividends = casesStructure.some((caseItem) =>
                    caseItem.forecasts.some((forecast) =>
                      toeConditions.some((_, conditionIndex) => {
                        const value = getWaterfallCaseYearInstrumentTrancheValue(
                          results,
                          caseItem.caseId,
                          forecast.forecastId,
                          instrument.instrumentId,
                          tranche.id,
                          (tranche) =>
                            tranche.ordinaryEquityMetrics?.conditions[conditionIndex]
                              ?.toeHurdleDetails?.cumulativeDividends ?? 0
                        );
                        return value !== 0 && value !== null && value !== undefined;
                      })
                    )
                  );

                  const hasToeBuybacks = casesStructure.some((caseItem) =>
                    caseItem.forecasts.some((forecast) =>
                      toeConditions.some((_, conditionIndex) => {
                        const value = getWaterfallCaseYearInstrumentTrancheValue(
                          results,
                          caseItem.caseId,
                          forecast.forecastId,
                          instrument.instrumentId,
                          tranche.id,
                          (tranche) =>
                            tranche.ordinaryEquityMetrics?.conditions[conditionIndex]
                              ?.toeHurdleDetails?.cumulativeRedemptions ?? 0
                        );
                        return value !== 0 && value !== null && value !== undefined;
                      })
                    )
                  );

                  const hasToeInterim = hasToeDividends || hasToeBuybacks;

                  return (
                    <Fragment key={tranche.id}>
                      <tr>
                        <th
                          className="table-primary__cell--header-tertiary"
                          data-testid={`tranche-header-${tranche.id}`}>
                          {l('_TrancheN', { n: index + 1 })}
                        </th>
                        <th
                          colSpan={columnsCount - 1}
                          className="table-primary__cell--header-tertiary"
                        />
                      </tr>
                      {(trancheHasIRR || trancheHasMoM) && (
                        <>
                          <TableSectionValuesPerForecast
                            title={l('_InitialSponsorCost')}
                            renderSpacer={false}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...amountRules}
                                value={getWaterfallCaseYearInstrumentTrancheValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  instrument.instrumentId,
                                  tranche.id,
                                  (tranche) => tranche.institutionalInvestment
                                )}
                                dataTestId={`initial-sponsor-cost-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                          {(trancheHasMoM || trancheHasIRR) &&
                            shouldRenderCumulativeInfusionRow && (
                              <>
                                <TableSectionValuesPerForecast
                                  title={l('_AddCumulativeInfusion')}
                                  renderSpacer={false}
                                  renderCell={({ caseItem, forecast }) => (
                                    <CellValue
                                      {...amountRules}
                                      value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                        results,
                                        caseItem.caseId,
                                        forecast.forecastId,
                                        instrument.instrumentId,
                                        tranche.id,
                                        momIndex,
                                        (condition) => condition?.cumulativeInfusions ?? 0
                                      )}
                                      dataTestId={`mom-hurdle-cumulative-infusion-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                    />
                                  )}
                                />
                                <TableSectionValuesPerForecast
                                  title={l('_TotalSponsorCost')}
                                  renderSpacer={false}
                                  additionalCellClassNames="table-primary__cell--strong"
                                  additionalTitleClassNames="table-primary__cell--strong"
                                  renderCell={({ caseItem, forecast }) => (
                                    <CellValue
                                      {...amountRules}
                                      value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                        results,
                                        caseItem.caseId,
                                        forecast.forecastId,
                                        instrument.instrumentId,
                                        tranche.id,
                                        momIndex,
                                        (condition) => condition?.totalSponsorCost
                                      )}
                                      dataTestId={`mom-hurdle-total-sponsor-cost-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                    />
                                  )}
                                />
                              </>
                            )}
                          {tranche.conditions
                            .filter(
                              (x) =>
                                x.targetMetric ===
                                  enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR) ||
                                x.targetMetric ===
                                  enumKeyByValue(TargetMetric, TargetMetric.InstitutionalMoM)
                            )
                            .map((condition, conditionIndex) => {
                              const isIrr =
                                condition.targetMetric ===
                                enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR);
                              const isMoM =
                                condition.targetMetric ===
                                enumKeyByValue(TargetMetric, TargetMetric.InstitutionalMoM);
                              const formatterProps = isIrr
                                ? percentageValueFormatterProps
                                : calculatedMultipleValueFormatterProps;
                              const metricValue = numberValueFormatter({
                                value: Number(condition.targetMetricTestValue),
                                ...formatterProps,
                              });
                              const title = l('_SponsorConditionPlaceholders', {
                                metric: isIrr ? l('_IRR') : l('_MoM'),
                                operator: Operator[condition.operator],
                                value: metricValue,
                                interpolation: { escapeValue: false },
                              });
                              const netTitle = l('_NetSponsorConditionPlaceholders', {
                                metric: isIrr ? l('_IRR') : l('_MoM'),
                                operator: Operator[condition.operator],
                                value: metricValue,
                                interpolation: { escapeValue: false },
                              });

                              const hasCumulative = (
                                field: keyof Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionMomHurdleDetailsDto
                              ) =>
                                isMoM &&
                                casesStructure.some((caseItem) =>
                                  caseItem.forecasts.some((forecast) => {
                                    const value =
                                      getWaterfallCaseYearInstrumentTrancheConditionValue(
                                        results,
                                        caseItem.caseId,
                                        forecast.forecastId,
                                        instrument.instrumentId,
                                        tranche.id,
                                        conditionIndex,
                                        (condition) => condition?.momHurdleDetails[field]
                                      );
                                    return value !== 0 && value !== null && value !== undefined;
                                  })
                                );

                              const hasCumulativeRedemption =
                                hasCumulative('cumulativeRedemptions');
                              const hasCumulativeCashInterest = hasCumulative('cumulativeCashPaid');
                              const hasCumulativeDividends = hasCumulative('cumulativeDividends');
                              const shouldRenderNetMomTotal =
                                isMoM &&
                                (hasCumulativeRedemption ||
                                  hasCumulativeCashInterest ||
                                  hasCumulativeDividends);

                              // this value is used in the IRRHurdleCalculations, need to be calculated precisely to prevent floating point issues
                              const targetMetricTestValue = new Decimal(
                                String(condition.targetMetricTestValue ?? 0)
                              )
                                .div(100)
                                .toString();

                              return (
                                <Fragment key={conditionIndex}>
                                  <TableSectionValuesPerForecast
                                    renderSpacer={false}
                                    title={title}
                                    rowTooltipInfo={
                                      isIrr
                                        ? l('_IrrNumbersCanBeClickedForFurtherInformation')
                                        : undefined
                                    }
                                    renderCell={({ caseItem, forecast }) => {
                                      const cellValue = isMoM
                                        ? getWaterfallCaseYearInstrumentTrancheConditionValue(
                                            results,
                                            caseItem.caseId,
                                            forecast.forecastId,
                                            instrument.instrumentId,
                                            tranche.id,
                                            conditionIndex,
                                            (condition) =>
                                              condition?.momHurdleDetails?.rawSponsorHurdleMom
                                          )
                                        : getWaterfallCaseYearInstrumentTrancheConditionValue(
                                            results,
                                            caseItem.caseId,
                                            forecast.forecastId,
                                            instrument.instrumentId,
                                            tranche.id,
                                            conditionIndex,
                                            (condition) => condition?.equivalentHurdleValue
                                          );
                                      return (
                                        <div
                                          onClick={
                                            isIrr && cellValue != null
                                              ? () =>
                                                  handleIrrCellClick(
                                                    caseItem.caseId,
                                                    forecast.forecastId,
                                                    instrument.instrumentId,
                                                    `${condition.targetMetric} - ${targetMetricTestValue}`,
                                                    title
                                                  )
                                              : undefined
                                          }>
                                          <CellValue
                                            {...amountRules}
                                            value={cellValue}
                                            className={classNames({
                                              'table-primary__cells': isIrr,
                                              [styles['clickable-cell']]:
                                                isIrr && cellValue != null,
                                            })}
                                            dataTestId={`condition-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                          />
                                        </div>
                                      );
                                    }}
                                  />
                                  {isMoM && (
                                    <>
                                      {hasCumulativeRedemption && (
                                        <TableSectionValuesPerForecast
                                          renderSpacer={false}
                                          title={l('_LessCumulativeRedemption')}
                                          renderCell={({ caseItem, forecast }) => (
                                            <CellValue
                                              {...amountRules}
                                              value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                                results,
                                                caseItem.caseId,
                                                forecast.forecastId,
                                                instrument.instrumentId,
                                                tranche.id,
                                                conditionIndex,
                                                (condition) =>
                                                  condition?.momHurdleDetails?.cumulativeRedemptions
                                              )}
                                              dataTestId={`cumulative-redemption-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                            />
                                          )}
                                        />
                                      )}
                                      {hasCumulativeCashInterest && (
                                        <TableSectionValuesPerForecast
                                          renderSpacer={false}
                                          title={l('_LessCumulativeCashInterest')}
                                          renderCell={({ caseItem, forecast }) => (
                                            <CellValue
                                              {...amountRules}
                                              value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                                results,
                                                caseItem.caseId,
                                                forecast.forecastId,
                                                instrument.instrumentId,
                                                tranche.id,
                                                conditionIndex,
                                                (condition) =>
                                                  condition?.momHurdleDetails?.cumulativeCashPaid
                                              )}
                                              dataTestId={`cumulative-cash-interest-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                            />
                                          )}
                                        />
                                      )}
                                      {hasCumulativeDividends && (
                                        <TableSectionValuesPerForecast
                                          renderSpacer={false}
                                          title={l('_LessCumulativeDividends')}
                                          renderCell={({ caseItem, forecast }) => (
                                            <CellValue
                                              {...amountRules}
                                              value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                                results,
                                                caseItem.caseId,
                                                forecast.forecastId,
                                                instrument.instrumentId,
                                                tranche.id,
                                                conditionIndex,
                                                (condition) =>
                                                  condition?.momHurdleDetails?.cumulativeDividends
                                              )}
                                              dataTestId={`cumulative-dividends-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                            />
                                          )}
                                        />
                                      )}
                                      {shouldRenderNetMomTotal && (
                                        <TableSectionValuesPerForecast
                                          renderSpacer={false}
                                          title={netTitle}
                                          additionalCellClassNames="table-primary__cell--strong"
                                          additionalTitleClassNames="table-primary__cell--strong"
                                          renderCell={({ caseItem, forecast }) => (
                                            <CellValue
                                              {...amountRules}
                                              value={getWaterfallCaseYearInstrumentTrancheConditionValue(
                                                results,
                                                caseItem.caseId,
                                                forecast.forecastId,
                                                instrument.instrumentId,
                                                tranche.id,
                                                conditionIndex,
                                                (condition) =>
                                                  condition?.momHurdleDetails?.conditionalValue
                                              )}
                                              dataTestId={`net-mom-total-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                            />
                                          )}
                                        />
                                      )}
                                    </>
                                  )}
                                </Fragment>
                              );
                            })}
                          {trancheHasConjunction && (
                            <TableSectionValuesPerForecast
                              renderSpacer={false}
                              title={l('_InstitutionHurdleActive')}
                              renderCell={({ caseItem, forecast }) => (
                                <CellValue
                                  {...amountRules}
                                  value={getWaterfallCaseYearInstrumentTrancheValue(
                                    results,
                                    caseItem.caseId,
                                    forecast.forecastId,
                                    instrument.instrumentId,
                                    tranche.id,
                                    (tranche) => tranche.activeHurdle
                                  )}
                                  dataTestId={`institution-hurdle-active-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                                />
                              )}
                            />
                          )}
                          <TableSectionValuesPerForecast
                            title={l('_LessSponsorDebtColon')}
                            renderSpacer={false}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...amountRules}
                                value={getWaterfallCaseYearInstrumentTrancheValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  instrument.instrumentId,
                                  tranche.id,
                                  (tranche) => tranche.lessSponsorDebt
                                )}
                                dataTestId={`less-sponsor-debt-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                          <TableSectionValuesPerForecast
                            title={l('_HurdleRemaining')}
                            renderSpacer={false}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...amountRules}
                                value={getWaterfallCaseYearInstrumentTrancheValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  instrument.instrumentId,
                                  tranche.id,
                                  (tranche) => tranche.hurdleRemaining
                                )}
                                dataTestId={`hurdle-remaining-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                          <TableSectionValuesPerForecast
                            title={l('_GrossUpPc')}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...enteredPercentageViewRules}
                                value={getCalculatedProjectValueInPercent(
                                  getWaterfallCaseYearInstrumentTrancheValue(
                                    results,
                                    caseItem.caseId,
                                    forecast.forecastId,
                                    instrument.instrumentId,
                                    tranche.id,
                                    (tranche) => tranche.grossUp
                                  )
                                )}
                                dataTestId={`gross-up-pc-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                        </>
                      )}
                      {!(trancheHasIRR || trancheHasMoM) &&
                        (toeConditions.length > 0 || hasToeInterim) && (
                          <Pwerm2WaterfallToeHurdleRows
                            results={results}
                            instrumentId={instrument.instrumentId}
                            trancheId={tranche.id}
                            toeConditions={toeConditions.map((c) => ({
                              operator: c.operator,
                              testValue: c.targetMetricTestValue ?? 0,
                            }))}
                            hasToeDividends={hasToeDividends}
                            hasToeBuybacks={hasToeBuybacks}
                          />
                        )}
                      <TableSectionValuesPerForecast
                        title={l('_EffectiveHurdle')}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...amountRules}
                            value={getWaterfallCaseYearInstrumentTrancheValue(
                              results,
                              caseItem.caseId,
                              forecast.forecastId,
                              instrument.instrumentId,
                              tranche.id,
                              (tranche) => tranche.ordinaryEquityHurdle
                            )}
                            dataTestId={`ordinary-equity-hurdle-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                          />
                        )}
                      />
                      <TableSectionValuesPerMultiple
                        title={l('_IsHurdleMet')}
                        renderCell={({ caseItem, forecast, multipleId }) => {
                          const isHurdleMetValue =
                            getWaterfallCaseYearMultipleInstrumentTrancheValue(
                              results,
                              caseItem.caseId,
                              forecast.forecastId,
                              multipleId!,
                              instrument.instrumentId,
                              tranche.id,
                              (tranche) => tranche.isHurdleMet
                            );
                          return (
                            <CellValue
                              className={
                                isHurdleMetValue
                                  ? styles['upper-text']
                                  : styles['upper-text-value-false']
                              }
                              alignment={Alignment.Right}
                              textTransform={TextTransform.Uppercase}
                              value={isHurdleMetValue}
                              dataTestId={`is-hurdle-met-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                            />
                          );
                        }}
                      />
                      {isIncrementalHurdleUsed && (
                        <TableSectionValuesPerMultiple
                          title={l('_OrdinaryEquityPostHurdle')}
                          renderCell={({ caseItem, forecast, multipleId }) => (
                            <CellValue
                              {...amountRules}
                              value={getWaterfallCaseYearMultipleInstrumentTrancheValue(
                                results,
                                caseItem.caseId,
                                forecast.forecastId,
                                multipleId!,
                                instrument.instrumentId,
                                tranche.id,
                                (tranche) => tranche.ordinaryEquityPostHurdle
                              )}
                              dataTestId={`ordinary-equity-post-hurdle-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                            />
                          )}
                        />
                      )}
                      <TableSectionValuesPerMultiple
                        title={l('_PcParticipation')}
                        testId={`${instrument.instrumentNarrative}-Tranche ${index + 1}`}
                        renderCell={({ caseItem, forecast, multipleId }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            plusSign={Boolean(
                              getWaterfallCaseYearMultipleInstrumentTrancheValue(
                                results,
                                caseItem.caseId,
                                forecast.forecastId,
                                multipleId!,
                                instrument.instrumentId,
                                tranche.id,
                                (tranche) => tranche.hasReceivedEnhancedParticipation
                              )
                            )}
                            dataTestId={`total-participation-pc-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                            value={getCalculatedProjectValueInPercent(
                              Number(
                                getWaterfallCaseYearMultipleInstrumentTrancheValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  multipleId!,
                                  instrument.instrumentId,
                                  tranche.id,
                                  (tranche) => tranche.totalParticipationPc
                                )
                              )
                            )}
                          />
                        )}
                        symbolTooltipInfo={{
                          instrumentId: instrument.instrumentId,
                          results,
                          trancheId: tranche.id,
                          symbolType: SymbolType.PlusSign,
                        }}
                      />
                      {instrument.capType === enumKeyByValue(CapType, CapType.Tranche) &&
                        tranche.capValue && (
                          <TableSectionValuesPerForecast
                            title={l('_MaximumPayoutCap')}
                            testId={`${instrument.instrumentNarrative}-Tranche ${index + 1}`}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...amountRules}
                                value={getWaterfallCaseYearInstrumentTrancheValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  instrument.instrumentId,
                                  tranche.id,
                                  (tranche) => tranche.proceedsCap
                                )}
                                dataTestId={`maximum-payout-cap-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                        )}
                      {instrument.capType === enumKeyByValue(CapType, CapType.Instrument) &&
                        instrument.capValue &&
                        (instrument.payoutLogic?.length ?? 0) === 1 && (
                          <TableSectionValuesPerForecast
                            title={l('_MaximumPayoutCap')}
                            testId={`${instrument.instrumentNarrative}-Tranche ${index + 1}`}
                            renderCell={({ caseItem, forecast }) => (
                              <CellValue
                                {...amountRules}
                                value={getWaterfallCaseYearInstrumentValue(
                                  results,
                                  caseItem.caseId,
                                  forecast.forecastId,
                                  instrument.instrumentId,
                                  (instrument) => instrument.proceedsCap
                                )}
                                dataTestId={`maximum-payout-cap-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
                              />
                            )}
                          />
                        )}
                      <TableSectionValuesPerMultiple
                        title={l('_DistributionsPaid')}
                        testId={`${instrument.instrumentNarrative}-Tranche ${index + 1}`}
                        renderCell={({ caseItem, forecast, multipleId }) => (
                          <CellValue
                            {...amountRules}
                            topLine={Boolean(
                              getWaterfallCaseYearMultipleInstrumentTrancheValue(
                                results,
                                caseItem.caseId,
                                forecast.forecastId,
                                multipleId!,
                                instrument.instrumentId,
                                tranche.id,
                                (tranche) => tranche.hasHitCap
                              )
                            )}
                            value={getWaterfallCaseYearMultipleInstrumentTrancheValue(
                              results,
                              caseItem.caseId,
                              forecast.forecastId,
                              multipleId!,
                              instrument.instrumentId,
                              tranche.id,
                              (tranche) => tranche.totalDistributions
                            )}
                            dataTestId={`distributions-paid-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                          />
                        )}
                        symbolTooltipInfo={{
                          instrumentId: instrument.instrumentId,
                          results,
                          trancheId: tranche.id,
                          symbolType: SymbolType.TopLine,
                        }}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_WeightedDistributions')}
                        highlight
                        renderSpacer={false}
                        additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                        additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...amountRules}
                            value={getWaterfallCaseYearInstrumentTrancheValue(
                              results,
                              caseItem.caseId,
                              forecast.forecastId,
                              instrument.instrumentId,
                              tranche.id,
                              (tranche) => tranche.weightedTotalDistributions
                            )}
                            dataTestId={`weighted-distributions-cell-${instrument.instrumentId}-${tranche.id}-${caseItem.caseId}-${forecast.forecastId}`}
                          />
                        )}
                      />
                      <TableSpacerRow />
                    </Fragment>
                  );
                })}
                {(instrument.payoutLogic?.length ?? 0) > 1 ? (
                  <>
                    <tr>
                      <th className="table-primary__cell--header-tertiary">
                        {l('_ExitDistributionsToName', {
                          name: instrument.instrumentNarrative,
                          interpolation: interpolationOption,
                        })}
                      </th>
                      <th
                        className="table-primary__cell--header-tertiary"
                        colSpan={columnsCount - 1}
                      />
                    </tr>
                    {instrument.capType === enumKeyByValue(CapType, CapType.Instrument) &&
                      instrument.capValue && (
                        <TableSectionValuesPerForecast
                          title={l('_MaximumPayoutCap')}
                          testId={`${instrument.instrumentNarrative}-Tranche 1`}
                          renderCell={({ caseItem, forecast }) => (
                            <CellValue
                              {...amountRules}
                              value={getWaterfallCaseYearInstrumentValue(
                                results,
                                caseItem.caseId,
                                forecast.forecastId,
                                instrument.instrumentId,
                                (instrument) => instrument.proceedsCap
                              )}
                              dataTestId={`maximum-payout-cap-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
                            />
                          )}
                        />
                      )}
                    <TableSectionValuesPerMultiple
                      title={l('_ExitDistributions')}
                      renderCell={({ caseItem, forecast, multipleId }) => (
                        <CellValue
                          {...amountRules}
                          topLine={Boolean(
                            getWaterfallCaseYearMultipleInstrumentValue(
                              results,
                              caseItem.caseId,
                              forecast.forecastId,
                              multipleId!,
                              instrument.instrumentId,
                              (instrument) => instrument.hasHitCap
                            )
                          )}
                          value={getWaterfallCaseYearMultipleInstrumentValue(
                            results,
                            caseItem.caseId,
                            forecast.forecastId,
                            multipleId!,
                            instrument.instrumentId,
                            (instrument) => instrument.totalDistributions
                          )}
                          dataTestId={`distributions-paid-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                        />
                      )}
                      symbolTooltipInfo={{
                        instrumentId: instrument.instrumentId,
                        results,
                        symbolType: SymbolType.TopLine,
                      }}
                    />
                    <TableSectionValuesPerForecast
                      title={l('_WeightedExitDistributions')}
                      highlight
                      additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                      additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                      renderCell={({ caseItem, forecast }) => (
                        <CellValue
                          {...amountRules}
                          value={getWaterfallCaseYearInstrumentValue(
                            results,
                            caseItem.caseId,
                            forecast.forecastId,
                            instrument.instrumentId,
                            (instrument) => instrument.weightedTotalDistributions
                          )}
                          dataTestId={`weighted-distributions-cell-${instrument.instrumentId}-${caseItem.caseId}-${forecast.forecastId}`}
                        />
                      )}
                    />
                  </>
                ) : undefined}
              </>
            )}
          </Fragment>
        ))}
        <Pwerm2WaterfallTotalPayoutSummaryPartialTable columnsCount={columnsCount} />
      </tbody>
    </>
  );
};
